/**
 * MobileTopNav — S5 mobile top navigation bar.
 *
 * Layout (dark / Instagram-derived):
 *   [+] ←  Vinnie.Autos  → [🚗 listings link]
 *
 * • Left: '+' button that opens the existing add-contact / duplicate-check flow
 *         (ContactCreateConfirmationModal + ContactDisambiguationModal) already
 *         present in MyPipeline.jsx – this component accepts onAddContact() prop
 *         so the parent (App.jsx) can host the modal state exactly as MyPipeline does.
 *
 * • Center: 'Vinnie.Autos' wordmark (logo asset available at /vinnie-logo.png).
 *
 * • Right: Car icon → plain external link to https://listings.vinnie.autos
 *          NOTE: deeper Listings integration (search, stock preview, etc.) is
 *          deferred future work. This is a static placeholder only – no backend
 *          calls, no new logic.
 *
 *   Logout icon → calls onLogout() prop (same handleLogout already used by the
 *          desktop header / old mobile drawer in App.jsx). Placed top-right,
 *          always visible — mobile previously had NO way to log out once the
 *          old desktop-only header/drawer was hidden by S5's mobile-only nav.
 *
 * Props:
 *   onAddContact  () => void   — called when '+' is tapped; caller manages modal state
 *   onLogout      () => void   — called when logout icon is tapped
 */

import React from 'react';
import { Plus, Car, LogOut } from 'lucide-react';

export default function MobileTopNav({ onAddContact, onLogout }) {
  return (
    <header style={styles.bar}>
      {/* Left: add-contact button */}
      <button
        style={styles.iconBtn}
        onClick={onAddContact}
        aria-label="Add new lead"
        title="Add new lead"
      >
        <Plus size={22} strokeWidth={2.2} color="#f1f5f9" />
      </button>

      {/* Centre: wordmark */}
      <div style={styles.wordmark}>
        <img
          src="/vinnie-logo.png"
          alt=""
          aria-hidden="true"
          style={styles.logoImg}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <span style={styles.wordmarkText}>
          Vinnie<span style={styles.wordmarkAccent}>.Autos</span>
        </span>
      </div>

      {/* Right: listings external link — static placeholder */}
      {/* TODO (future): deeper Listings integration (stock search, live pricing,
          lead-to-vehicle matching) is deferred. For now this is a plain anchor. */}
      <div style={styles.rightGroup}>
        <a
          href="https://listings.vinnie.autos"
          target="_blank"
          rel="noopener noreferrer"
          style={styles.iconLink}
          aria-label="Open Vinnie Listings site"
          title="Listings (opens in new tab)"
        >
          <Car size={22} strokeWidth={1.8} color="#f1f5f9" />
        </a>

        <button
          style={styles.iconBtn}
          onClick={onLogout}
          aria-label="Log out"
          title="Log out"
        >
          <LogOut size={20} strokeWidth={2} color="#f1f5f9" />
        </button>
      </div>
    </header>
  );
}

const styles = {
  bar: {
    position: 'sticky',
    top: 0,
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '52px',
    padding: '0 8px',
    background: 'rgba(11, 18, 32, 0.92)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    borderBottom: '1px solid rgba(44, 53, 78, 0.55)',
  },
  rightGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
  },
  iconBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    // Increase tap target to 44px recommended minimum
    padding: '2px',
  },
  iconLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    textDecoration: 'none',
    padding: '2px',
  },
  wordmark: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    userSelect: 'none',
  },
  logoImg: {
    height: '26px',
    width: 'auto',
    display: 'block',
  },
  wordmarkText: {
    fontFamily: "'Lato', 'Noto Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: '18px',
    fontWeight: 700,
    color: '#f1f5f9',
    letterSpacing: '-0.2px',
    lineHeight: 1,
  },
  wordmarkAccent: {
    color: 'var(--brand-primary, #317eeb)',
  },
};
